import dayjs from 'dayjs';
import type { UndefinedOnPartialDeep } from 'type-fest';

import type { Service } from '@repo/types/service';
import type { TimeWindow, TimeWindowResponseItem } from '@repo/types/time-windows';
import { computeDayKey } from '@repo/utils';

const getTimeWindowsForDayKey = (
    timeWindows: TimeWindowResponseItem[],
    selectedDayKey: string,
    timeZone: string
): TimeWindow[] => {
    return timeWindows.filter((timeWindow) => {
        return computeDayKey(timeWindow.fromDateTimeUtc, timeZone) === selectedDayKey;
    });
};

export const findTimeWindowById = (
    apiTimeWindows: TimeWindowResponseItem[],
    selectedTimeWindowId: string
): TimeWindowResponseItem => {
    const timeWindow = apiTimeWindows.find((tw) => tw.timeWindowId === selectedTimeWindowId);
    if (!timeWindow) {
        throw new Error(`Time window not found: ${selectedTimeWindowId}`);
    }
    return timeWindow;
};

interface MonthSelectionState {
    selectedDayKey: string;
    timeWindowId: string | undefined;
}

/**
 * Determines what date and time window should be selected when the active month changes.
 * - If the current delivery date is in the active month, select it and preselect the current time window
 * - Otherwise, select the first available date (or month start if none) and clear selection
 */
export const getInitialMonthSelection = ({
    activeMonthStartDate,
    availableDates,
    currentDeliveryDateUtc,
    currentTimeWindowId,
    timeZone,
}: {
    activeMonthStartDate: dayjs.Dayjs;
    availableDates: dayjs.Dayjs[];
    currentDeliveryDateUtc: dayjs.Dayjs;
    currentTimeWindowId: string;
    timeZone: string;
}): MonthSelectionState => {
    const isCurrentDateInMonth = currentDeliveryDateUtc.tz(timeZone).isSame(activeMonthStartDate.tz(timeZone), 'month');

    if (currentTimeWindowId && isCurrentDateInMonth) {
        return {
            selectedDayKey: computeDayKey(currentDeliveryDateUtc.toISOString(), timeZone),
            timeWindowId: currentTimeWindowId,
        };
    }

    const firstAvailableDate = availableDates.find((date) =>
        date.tz(timeZone).isSame(activeMonthStartDate.tz(timeZone), 'month')
    );
    return {
        selectedDayKey: computeDayKey((firstAvailableDate ?? activeMonthStartDate).toISOString(), timeZone),
        timeWindowId: undefined,
    };
};

export const getMonthStartFromDate = (date: Date) =>
    dayjs.utc().year(date.getFullYear()).month(date.getMonth()).date(date.getDate()).startOf('day');

export const getAvailableTimeWindowDates = (timeWindows: TimeWindowResponseItem[], timeZone: string) => {
    const availableTimeWindowDates: dayjs.Dayjs[] = [];
    const seenDays = new Set<string>();

    for (const timeWindow of timeWindows) {
        const fromDateUtc = dayjs.utc(timeWindow.fromDateTimeUtc);
        const dayKey = fromDateUtc.tz(timeZone).format('YYYY-MM-DD');

        if (seenDays.has(dayKey)) {
            continue;
        }

        seenDays.add(dayKey);
        availableTimeWindowDates.push(fromDateUtc);
    }

    return availableTimeWindowDates.sort((left, right) => left.diff(right));
};

export const getDisplayedTimeWindows = ({
    timeWindows,
    selectedDayKey,
    timeZone,
    serviceTimeWindow,
    currentDeliveryDayKey,
}: {
    timeWindows: TimeWindowResponseItem[];
    selectedDayKey: string | null;
    timeZone: string;
    serviceTimeWindow: UndefinedOnPartialDeep<Service>['timeWindow'];
    currentDeliveryDayKey: string;
}) => {
    if (!selectedDayKey) {
        return [];
    }

    const selectedDayTimeWindows = getTimeWindowsForDayKey(timeWindows, selectedDayKey, timeZone);

    if (selectedDayKey !== currentDeliveryDayKey || !serviceTimeWindow) {
        return selectedDayTimeWindows;
    }

    return [
        serviceTimeWindow,
        ...selectedDayTimeWindows.filter((timeWindow) => timeWindow.timeWindowId !== serviceTimeWindow.timeWindowId),
    ];
};
